import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import { useSelector } from "react-redux";
import { useMutation, useQuery } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import toast from "react-hot-toast";
import { styled } from "@mui/material/styles";
import TeamMembersDialog from "./TeamMembersDialog";
import ChatOrderSettingsDialog from "./ChatOrderSettingsDialog";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";

// --- priority color options ---
const priorityOptions = [
  {
    value: "respond now",
    label: "respond now",
    bgcolor: "#ff3d00", // vivid orange-red
  },
  {
    value: "respond next",
    label: "respond next",
    bgcolor: "#ffa000", // vivid orange
  },
  {
    value: "respond later",
    label: "respond later",
    bgcolor: "#00ff00", // vivid green
  },
  {
    value: "respond after",
    label: "respond after",
    bgcolor: "#ccff00", // vivid yellow-green
  },
];

export default function ChatSettingsModal({ IsMaxView = false }) {
  const user = useSelector((state) => state.auth.user);
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const { hasPermission, isOwnBusiness } = useBusinessPermissions();
  const canManageChats = hasPermission("manageChats");
  const isDisabled = !isOwnBusiness && !canManageChats;

  const selectedBusiness =
    user?.business?.filter(
      (business) => business._id === selectedBusinessId
    )[0] || null;

  const features = selectedBusiness?.features || [];
  // console.log("features------->", features);
  const enebleAi =
    features.find((feature) => feature?.featureKey === "ai_messages")?.enabled || false;
  const { data: locationList, refetch } = useQuery(
    ["locationList", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        ApiEndPoints.BUSINESS_LOCATION.getAll + "/" + selectedBusinessId,
        { signal }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      // cacheTime: 30 * 60 * 1000,
    }
  );

  const { data: teamMemberList } = useQuery(
    ["teamMemberList", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.post(
        ApiEndPoints.TEAM_LINKING.get_team_member(selectedBusinessId),
        { signal }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      // cacheTime: 30 * 60 * 1000,
    }
  );
  console.log("team member list", teamMemberList);
  // const { data: linkedUserList = [] } = useQuery(
  //   ["linkedUserList", selectedBusinessId],
  //   async () => {
  //     // const res = await axiosInstance.get(ApiEndPoints.TEAM_LINKING.get);
  // const url = ApiEndPoints.TEAM_LINKING.get_team_member(
  //   selectedBusinessId
  // );

  //     const body = {
  //       locationIds: [locationToEdit._id],
  //     };
  //     const res = await axiosInstance.post(url, body);
  //     return res.data.data.employees;
  //   },
  //   {
  //     enabled: locationToEdit !== null, // Only run when isNewAdd is true
  //   }
  // );

  const { mutate: updateSetting } = useMutation({
    mutationFn: async ({ locationId, payload }) => {
      const res = await axiosInstance.patch(
        `${ApiEndPoints.BUSINESS_LOCATION.setting}/${locationId}`,
        payload
      );
      return res.data;
    },

    onSuccess: (res) => {
      if (res.status === "success") {
        toast.success("chat settings updated");
        refetch();
      } else {
        toast.error(res.message || "update failed");
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "something went wrong");
    },
  });

  // State
  const [location, setLocation] = useState("");
  const [allowEdit, setAllowEdit] = useState(false);
  const [allowDelete, setAllowDelete] = useState(false);
  const [enableAi, setEnableAi] = useState(false);
  const [chatCategories, setChatCategories] = useState([]);

  // auto reply dialog state
  const [autoReplyDialogOpen, setAutoReplyDialogOpen] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(-1);
  const [customMessage, setCustomMessage] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);

  // predefined auto reply messages
  const predefinedMessages = [
    "i will talk later",
    "thank you will be back",
    "bye see you later",
    "hi, how are you hope you are doing well",
    "will add later",
  ];

  const [customMessages, setCustomMessages] = useState([]);

  // default location
  const defaultLocationObj = useMemo(() => {
    const locations = locationList?.locations || [];
    return locations.find((loc) => loc.isPrimaryAddress) || locations[0];
  }, [locationList]);

  useEffect(() => {
    if (defaultLocationObj && !location) {
      setLocation(defaultLocationObj._id);
      setAllowEdit(defaultLocationObj.enableMessageEditing ?? false);
      setAllowDelete(defaultLocationObj.enableMessageDeleting ?? false);
      setEnableAi(defaultLocationObj.enableAi ?? false);
      // ensure chat categories have required fields
      const categories = (defaultLocationObj.chatCategories || []).map(
        (cat) => ({
          ...cat,
          // keep the full categoryid object structure as received from api
          priority: cat.priority || "respond now",
          autoReply: cat.autoReply || "unavailable",
          message: cat.message || "",
          department: cat.department || [],
          teamMemberId: cat.teamMemberId || [],
        })
      );
      setChatCategories(categories);
    }
  }, [defaultLocationObj, location]);

  // --- responsive modal styles ---
  const modalSx = useMemo(() => {
    if (IsMaxView) {
      return {
        width: "100vw",
        minHeight: "100vh",
        bgcolor: "#fff",
        fontFamily: "inherit",
        display: "flex",
        flexDirection: "column",
        p: { xs: 3, md: 5 },
        boxSizing: "border-box",
        borderRadius: 3,
        overflow: "auto",
      };
    } else {
      return {
        width: { xs: 370, sm: 400 },
        minWidth: 0,
        maxWidth: "96vw",
        maxHeight: "90vh",
        bgcolor: "#fff",
        fontFamily: "inherit",
        display: "flex",
        flexDirection: "column",
        p: 2,
        boxSizing: "border-box",
        borderRadius: 4,
        overflowY: "auto",
        overflowX: "hidden",
        boxShadow: "0 2px 14px rgba(44,0,80,0.14)",
      };
    }
  }, [IsMaxView]);

  const tableContainerSx = useMemo(
    () => ({
      width: "100%",
      maxWidth: "100vw",
      overflowX: "auto",
      bgcolor: "#f7f2fa",
      borderRadius: 2,
      mt: IsMaxView ? 1 : 0.5,
      px: 0,
      pb: IsMaxView ? 2 : 1,
    }),
    [IsMaxView]
  );

  const updateLocationSettings = (updates, customChatCategories = null) => {
    if (location) {
      // use custom categories if provided, otherwise use state
      const categoriesToUse = customChatCategories || chatCategories;

      // only send the required fields, explicitly excluding previousmessage and other unwanted fields
      const validatedChatCategories = categoriesToUse.map((cat) => ({
        name: cat.name,
        categoryId: cat.categoryId?._id || cat.categoryId, // pass categoryid._id
        priority: cat.priority || "respond now",
        autoReply: cat.autoReply || "unavailable",
        message: cat.message || "",
        department: cat.department || [],
        teamMemberId: cat.teamMemberId || [],
        isActive: cat.isActive !== undefined ? cat.isActive : true,
      }));

      const payload = {
        enableMessageEditing: allowEdit,
        enableMessageDeleting: allowDelete,
        enableAi: enableAi,
        chatCategories: validatedChatCategories,
        ...updates,
      };
      updateSetting({
        locationId: location,
        payload: payload,
      });
    }
  };

  // priority dropdown logic

  // auto reply dialog handlers
  const handleAutoReplyClick = (categoryIndex) => {
    setSelectedCategoryIndex(categoryIndex);
    setAutoReplyDialogOpen(true);
    setShowCustomInput(false);
    // pass current message value to custom input
    const currentCategory = chatCategories[categoryIndex];
    if (currentCategory?.autoReply === "custom" && currentCategory?.message) {
      setCustomMessage(currentCategory.message);
    } else {
      setCustomMessage("");
    }
  };

  const handleMessageSelect = (message) => {
    if (message === "custom") {
      setShowCustomInput(true);
      return;
    }

    const updatedCategories = [...chatCategories];
    updatedCategories[selectedCategoryIndex] = {
      ...updatedCategories[selectedCategoryIndex],
      // keep the full categoryid object structure
      autoReply: message === "will add later" ? "ai" : "custom",
      message: message === "will add later" ? "" : message,
    };
    setChatCategories(updatedCategories);
    // pass the updated categories directly to ensure we send the correct data
    updateLocationSettings({}, updatedCategories);
    setAutoReplyDialogOpen(false);
  };

  const handleAddCustomMessage = () => {
    if (customMessage.trim()) {
      const newCustomMessages = [...customMessages, customMessage.trim()];
      setCustomMessages(newCustomMessages);

      const updatedCategories = [...chatCategories];
      updatedCategories[selectedCategoryIndex] = {
        ...updatedCategories[selectedCategoryIndex],
        // keep the full categoryid object structure
        autoReply: "custom",
        message: customMessage.trim(),
      };
      setChatCategories(updatedCategories);
      // pass the updated categories directly to ensure we send the correct data
      updateLocationSettings({}, updatedCategories);

      setCustomMessage("");
      setShowCustomInput(false);
      setAutoReplyDialogOpen(false);
    }
  };

  // team members dialog state
  const [teamMembersDialogOpen, setTeamMembersDialogOpen] = useState(false);
  const [selectedTeamCategoryIndex, setSelectedTeamCategoryIndex] =
    useState(-1);

  // chat order settings dialog state
  const [chatOrderSettingsDialogOpen, setChatOrderSettingsDialogOpen] =
    useState(false);

  // team members dialog handlers
  const handleTeamMembersClick = (categoryIndex) => {
    setSelectedTeamCategoryIndex(categoryIndex);
    setTeamMembersDialogOpen(true);
  };

  const handleSaveTeamMembers = (updatedCategories) => {
    // update local state immediately
    setChatCategories(updatedCategories);

    // call the update api
    updateLocationSettings({}, updatedCategories);
  };

  const handleSaveChatOrderSettings = (settings) => {
    console.log("saving chat order settings:", settings);
    // here you can implement the api call to save the order settings
    toast.success("chat order settings updated  ");
  };

  // demo icons for team members and auto reply (use real icons in prod)
  const TeamIcon = () => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#e0eafc",
        color: "#2b63e3",
        borderRadius: "4px",
        width: 24,
        height: 22,
        fontWeight: 600,
        fontSize: "1em",
        mr: 0.4,
      }}
    >
      🏢
    </Box>
  );
  const PeopleIcon = () => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#eaf7fa",
        color: "#135b76",
        borderRadius: "4px",
        width: 24,
        height: 22,
        fontWeight: 600,
        fontSize: "1em",
        mr: 0.4,
      }}
    >
      👥
    </Box>
  );
  const ChatIcon = () => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#eafae7",
        color: "#1ebc4b",
        borderRadius: "4px",
        width: 24,
        height: 22,
        fontWeight: 600,
        fontSize: "1em",
        mr: 0.4,
      }}
    >
      💬
    </Box>
  );

  return (
    <Box sx={modalSx}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: IsMaxView ? "1.2rem" : "1rem",
          mb: IsMaxView ? 3 : 2,
        }}
      >
        business chat settings
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 320 }}>
        <Select
          disabled={isDisabled}
          value={location || ""}
          onChange={(e) => {
            const selectedId = e.target.value;
            setLocation(selectedId);
            const selected = locationList?.locations?.find(
              (loc) => loc._id === selectedId
            );
            if (selected) {
              setAllowEdit(selected.enableMessageEditing ?? false);
              setAllowDelete(selected.enableMessageDeleting ?? false);
              setEnableAi(selected.enableAi ?? false);
              // ensure chat categories have required fields
              const categories = (selected.chatCategories || []).map((cat) => ({
                ...cat,
                // keep the full categoryid object structure as received from api
                priority: cat.priority || "respond now",
                autoReply: cat.autoReply || "unavailable",
                message: cat.message || "",
                department: cat.department || [],
                teamMemberId: cat.teamMemberId || [],
              }));
              setChatCategories(categories);
            }
          }}
          displayEmpty
          sx={{
            bgcolor: "#f7f2fa",
            color: "#7c28d6",
            fontSize: IsMaxView ? "1.13em" : "1em",
            fontWeight: 500,
            borderRadius: 2,
          }}
        >
          <MenuItem value="" disabled sx={{ color: "#999" }}>
            default location
          </MenuItem>
          {locationList?.locations?.map((loc) => (
            <MenuItem key={loc._id} value={loc._id} sx={{ color: "#7c28d6" }}>
              {loc.businessLocationName}
              {loc.isPrimaryAddress && (
                <Chip
                  size="small"
                  label="primary"
                  sx={{ ml: 1, height: 20, fontSize: "0.7em" }}
                  color="primary"
                />
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography sx={{ color: "#7c28d6", fontWeight: 600 }}>
        business locations are publicly visible
      </Typography>
      <Typography
        sx={{
          color: "#333",
          fontSize: "0.97em",
          mb: 2,
          whiteSpace: "pre-line",
        }}
      >
        accepting all marketing and{"\n"}3rd party advertisement
      </Typography>

      <Typography
        sx={{ fontWeight: 700, color: "#222", mt: 1, mb: 1 }}
        variant="subtitle1"
      >
        business chat
      </Typography>
      <Typography sx={{ color: "#7c28d6", fontWeight: 700, mb: 0.5 }}>
        allowing editing of messages?
      </Typography>
      <RadioGroup
        row
        value={allowEdit ? "yes" : "no"}
        onChange={(e) => {
          if (isDisabled) return;
          const newValue = e.target.value === "yes";
          setAllowEdit(newValue);
          updateLocationSettings({ enableMessageEditing: newValue });
        }}
      >
        <FormControlLabel
          value="yes"
          control={<Radio sx={{ color: "#7c28d6" }} disabled={isDisabled} />}
          label="yes"
        />
        <FormControlLabel
          value="no"
          control={<Radio sx={{ color: "#7c28d6" }} disabled={isDisabled} />}
          label="no"
        />
      </RadioGroup>

      <Typography sx={{ color: "#7c28d6", fontWeight: 700, mb: 0.5, mt: 1 }}>
        allow deleting of messages?
      </Typography>
      <RadioGroup
        row
        value={allowDelete ? "yes" : "no"}
        onChange={(e) => {
          if (isDisabled) return;
          const newValue = e.target.value === "yes";
          setAllowDelete(newValue);
          updateLocationSettings({ enableMessageDeleting: newValue });
        }}
      >
        <FormControlLabel
          value="yes"
          control={<Radio sx={{ color: "#7c28d6" }} disabled={isDisabled} />}
          label="yes"
        />
        <FormControlLabel
          value="no"
          control={<Radio sx={{ color: "#7c28d6" }} disabled={isDisabled} />}
          label="no"
        />
      </RadioGroup>

      {enebleAi && (
        <>
          <Typography
            sx={{ color: "#7c28d6", fontWeight: 700, mb: 0.5, mt: 1 }}
          >
            enable ai
          </Typography>
          <RadioGroup
            row
            value={enableAi ? "yes" : "no"}
            onChange={(e) => {
              if (isDisabled) return;
              const newValue = e.target.value === "yes";
              setEnableAi(newValue);
              updateLocationSettings({ enableAi: newValue });
            }}
          >
            <FormControlLabel
              value="yes"
              control={<Radio sx={{ color: "#7c28d6" }} disabled={isDisabled} />}
              label="yes"
            />
            <FormControlLabel
              value="no"
              control={<Radio sx={{ color: "#7c28d6" }} disabled={isDisabled} />}
              label="no"
            />
          </RadioGroup>
        </>
      )}

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ mt: 2, color: "#7c28d6", fontWeight: 700, fontSize: "1.1em" }}
      >
        chat order settings
        <Tooltip title="order settings for chat" arrow>
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </Tooltip>
        <IconButton
          size="small"
          sx={{ color: "#7c28d6", ml: -1 }}
          onClick={() => {
            if (isDisabled) return;
            setChatOrderSettingsDialogOpen(true);
          }}
          disabled={isDisabled}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 3, mb: 1 }}>
        <Typography
          sx={{ color: "#7c28d6", fontWeight: 700, fontSize: "1.08em" }}
        >
          business chat categories
        </Typography>
        <Tooltip title="manage chat categories and auto reply" arrow>
          <InfoOutlinedIcon sx={{ fontSize: 17 }} />
        </Tooltip>
      </Stack>

      <Box sx={tableContainerSx}>
        <Table size="small" sx={{ minWidth: 700, width: "100%" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#e6d0fa" }}>
              <TableCell
                sx={{
                  color: "#7c28d6",
                  fontWeight: 700,
                  border: 0,
                  textTransform: "lowercase",
                  fontSize: "15px !important",
                }}
              >
                chat category
              </TableCell>
              <TableCell
                sx={{
                  color: "#7c28d8",
                  fontWeight: 700,
                  border: 0,
                  textTransform: "lowercase",
                  fontSize: "15px !important",
                }}
              >
                status
              </TableCell>
              <TableCell
                sx={{
                  color: "#7c28d6",
                  fontWeight: 700,
                  border: 0,
                  textTransform: "lowercase",
                  fontSize: "15px !important",
                }}
              >
                priority
              </TableCell>
              <TableCell
                sx={{
                  color: "#7c28d6",
                  fontWeight: 700,
                  border: 0,
                  textTransform: "lowercase",
                  fontSize: "15px !important",
                }}
              >
                auto reply
              </TableCell>
              <TableCell
                sx={{
                  color: "#7c28d6",
                  fontWeight: 700,
                  border: 0,
                  textTransform: "lowercase",
                  fontSize: "15px !important",
                }}
              >
                team members
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chatCategories.map((cat, index) => {
              return (
                <TableRow key={cat.name || index} sx={{ border: 0 }}>
                  <TableCell sx={{ border: 0, fontSize: "1em", color: "#333" }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      {cat.categoryId?.name || cat.name}
                    </Stack>
                  </TableCell>
                  {/* Status */}
                  <TableCell sx={{ border: 0, minWidth: 100 }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        disabled={isDisabled}
                        value={cat.isActive !== false ? "active" : "inactive"}
                        onChange={(e) => {
                          if (isDisabled) return;
                          const newStatus = e.target.value === "active";
                          const updatedCategories = [...chatCategories];
                          updatedCategories[index] = {
                            ...cat,
                            isActive: newStatus,
                          };
                          setChatCategories(updatedCategories);
                          updateLocationSettings({}, updatedCategories);
                        }}
                        sx={{
                          bgcolor: "#fff",
                          color: cat.isActive !== false ? "#2e7d32" : "#d32f2f",
                          fontWeight: 600,
                          fontSize: "0.8em",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          "& .MuiSelect-select": {
                            padding: "6px 12px",
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              cat.isActive !== false ? "#4caf50" : "#f44336",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              cat.isActive !== false ? "#45a049" : "#da190b",
                          },
                        }}
                      >
                        <MenuItem
                          value="active"
                          sx={{ color: "#222", fontWeight: 500, minHeight: 38 }}
                        >
                          active
                        </MenuItem>
                        <MenuItem
                          value="inactive"
                          sx={{ color: "#222", fontWeight: 500, minHeight: 38 }}
                        >
                          inactive
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  {/* Priority */}
                  <TableCell sx={{ border: 0, minWidth: 140 }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        disabled={isDisabled}
                        value={cat.priority || "respond later"}
                        onChange={(e) => {
                          if (isDisabled) return;
                          const updatedCategories = [...chatCategories];
                          updatedCategories[index] = {
                            ...cat,
                            priority: e.target.value,
                            autoReply: cat.autoReply || "unavailable",
                          };
                          setChatCategories(updatedCategories);
                          updateLocationSettings({}, updatedCategories);
                        }}
                        sx={{
                          bgcolor: (() => {
                            const option = priorityOptions.find(
                              (opt) => opt.value === cat.priority
                            );
                            if (!option) return "#e8f5e8";
                            // Create light versions of priority colors
                            const lightColors = {
                              "respond now": "#ffe8e0", // Light orange-red
                              "respond next": "#fff3e0", // Light orange
                              "respond later": "#e8f5e8", // Light green
                              "respond after": "#f0f8e0", // Light yellow-green
                            };
                            return lightColors[option.value] || "#e8f5e8";
                          })(),
                          color: (() => {
                            const option = priorityOptions.find(
                              (opt) => opt.value === cat.priority
                            );
                            return option ? option.bgcolor : "#4caf50";
                          })(),
                          fontWeight: 600,
                          fontSize: "0.8em",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          "& .MuiSelect-select": {
                            padding: "6px 12px",
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: (() => {
                              const option = priorityOptions.find(
                                (opt) => opt.value === cat.priority
                              );
                              return option ? option.bgcolor : "#4caf50";
                            })(),
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: (() => {
                              const option = priorityOptions.find(
                                (opt) => opt.value === cat.priority
                              );
                              return option ? option.bgcolor : "#4caf50";
                            })(),
                          },
                        }}
                      >
                        {priorityOptions.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            value={opt.value}
                            sx={{
                              color: "#222",
                              fontWeight: 500,
                              minHeight: 38,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                bgcolor: opt.bgcolor,
                                color: "#fff",
                                borderRadius: "4px",
                                display: "inline-block",
                                mr: 1.4,
                              }}
                            />
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  {/* Auto Reply */}
                  <TableCell
                    sx={{
                      border: 0,
                      minWidth: 210,
                      fontSize: "1em",
                      color: "#333",
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography sx={{ fontSize: "0.9em", color: "#333" }}>
                        {cat.autoReply === "unavailable"
                          ? "we are unavailable right now"
                          : cat.autoReply === "ai"
                          ? "will add later"
                          : cat.autoReply === "custom"
                          ? cat.message || "set auto reply"
                          : "set auto reply"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isDisabled) return;
                          handleAutoReplyClick(index);
                        }}
                        disabled={isDisabled}
                        sx={{
                          p: 0.5,
                          color: "#7c28d6",
                          "&:hover": { bgcolor: "#f3e5f5" },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  {/* Team Members */}
                  <TableCell sx={{ border: 0, minWidth: 130 }}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <ChatIcon />
                      <TeamIcon />
                      <PeopleIcon />
                      <Typography
                        variant="caption"
                        sx={{ color: "#888", ml: 0.2 }}
                      >
                        ({cat.department?.length || 0})
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#bbb", ml: 0.2 }}
                      >
                        ({cat.teamMemberId?.length || 0})
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isDisabled) return;
                          handleTeamMembersClick(index);
                        }}
                        disabled={isDisabled}
                        sx={{
                          p: 0.5,
                          color: "#7c28d6",
                          "&:hover": { bgcolor: "#f3e5f5" },
                          ml: 0.5,
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {chatCategories.length === 0 && location && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "#f0f0f0",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#666", fontSize: "0.9em" }}>
            no chat categories found for this location
          </Typography>
        </Box>
      )}

      {!location && locationList?.locations?.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "#fff3cd",
            borderRadius: 1,
            border: "1px solid #ffeaa7",
          }}
        >
          <Typography sx={{ color: "#856404", fontSize: "0.9em" }}>
            please select a location to view chat settings
          </Typography>
        </Box>
      )}

      {/* Auto Reply Dialog */}
      <Dialog
        open={autoReplyDialogOpen}
        onClose={() => setAutoReplyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            color: "#7c28d6",
            fontWeight: 700,
            borderBottom: "1px solid #e0e0e0",
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          chat category -{" "}
          {chatCategories[selectedCategoryIndex]?.categoryId.name || ""}
          <IconButton
            onClick={() => setAutoReplyDialogOpen(false)}
            sx={{
              color: "#7c28d6",
              "&:hover": { bgcolor: "#f3e5f5" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {!showCustomInput ? (
            <List>
              {predefinedMessages.map((message, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton onClick={() => handleMessageSelect(message)}>
                    <ListItemText
                      primary={message}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "0.9em",
                          color: "#333",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {customMessages.map((message, index) => (
                <ListItem key={`custom-${index}`} disablePadding>
                  <ListItemButton onClick={() => handleMessageSelect(message)}>
                    <ListItemText
                      primary={message}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "0.9em",
                          color: "#333",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMessageSelect("custom")}>
                  <ListItemText
                    primary="custom"
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "0.9em",
                        color: "#7c28d6",
                        fontWeight: 500,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                {customMessage ? "edit custom message:" : "add custom message:"}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="enter your custom message..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.9em",
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          {showCustomInput && (
            <Button
              onClick={handleAddCustomMessage}
              variant="contained"
              sx={{
                bgcolor: "#7c28d6",
                "&:hover": { bgcolor: "#5a1b8a" },
                textTransform: "none",
                px: 3,
              }}
            >
              save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Team Members Dialog */}
      <TeamMembersDialog
        open={teamMembersDialogOpen}
        onClose={() => setTeamMembersDialogOpen(false)}
        category={chatCategories[selectedTeamCategoryIndex]}
        teamMemberList={teamMemberList}
        onSave={handleSaveTeamMembers}
        chatCategories={chatCategories}
        categoryIndex={selectedTeamCategoryIndex}
        selectedLocationId={location}
      />

      {/* Chat Order Settings Dialog */}
      <ChatOrderSettingsDialog
        open={chatOrderSettingsDialogOpen}
        onClose={() => setChatOrderSettingsDialogOpen(false)}
        onSave={handleSaveChatOrderSettings}
        locationId={location}
        locationName={
          locationList?.locations?.find((loc) => loc._id === location)
            ?.businessLocationName || "unknown location"
        }
        locationData={locationList?.locations?.find(
          (loc) => loc._id === location
        )}
        currentSettings={{}}
        onRefetch={refetch}
      />
    </Box>
  );
}
